import { request } from '../../api/client';
import type { DetailSection } from '../types';

/**
 * Thin wrappers over the PUBLIC (unauthenticated) catalogue API. These endpoints are the SINGLE
 * SOURCE OF TRUTH for the marketing site's product data — the repository maps their output into the
 * UI `Product`/`Category` shapes. Nothing here is hardcoded product content.
 */

/** One catalogue row as served by GET /public/products (a single variant/SKU). */
export interface PublicProductDto {
  id: string;
  sku: string;
  groupCode: string | null;
  variantLabel: string | null;
  name: string;
  hsnCode: string | null;
  uomCode: string;
  packSize: number;
  moq: number;
  mrp: number;
  imageUrl: string | null;
  imageUrls: string[] | null;
  videoUrl: string | null;
  videoUrls: string[] | null;
  description: string | null;
  // Group-level marketing metadata (denormalized onto every variant of the group).
  category: string | null;
  crops: string[];
  benefitTags: string[];
  applicationTags: string[];
  tagline: string | null;
  summary: string | null;
  highlights: string[];
  isActive: boolean;
}

export interface PublicCategoryDto {
  slug: string;
  name: string;
  shortName: string;
  tagline: string;
  description: string;
  accent: string;
  icon: string;
}

export interface PublicDetailsDto {
  highlights: string[];
  resources: { label: string; url: string; lang?: string }[];
  sections: DetailSection[];
  category: string | null;
  crops: string[];
  benefitTags: string[];
  applicationTags: string[];
  tagline: string | null;
  summary: string | null;
}

/** Active products (all variants). Fetches a large page so the whole small catalogue arrives once. */
export async function fetchPublicProducts(): Promise<PublicProductDto[]> {
  const res = await request<{ data: PublicProductDto[] }>({
    url: '/public/products',
    params: { pageSize: 500, page: 1 },
  });
  return res?.data ?? [];
}

export async function fetchPublicCategories(): Promise<PublicCategoryDto[]> {
  const res = await request<{ data: PublicCategoryDto[] }>({ url: '/public/categories' });
  return res?.data ?? [];
}

/** Rich admin-authored detail for one product master (by any variant id). */
export async function fetchPublicDetails(productId: string): Promise<PublicDetailsDto> {
  return request<PublicDetailsDto>({ url: `/public/products/${productId}/details` });
}

/** Multi-language catalogue PDF URLs keyed by language, e.g. { en: "/v1/files/...pdf" }. */
export async function fetchPublicCatalogues(productId: string): Promise<Record<string, string>> {
  try {
    return (await request<Record<string, string>>({ url: `/public/products/${productId}/catalogues` })) ?? {};
  } catch {
    return {};
  }
}
